import { useEffect, useRef, ReactNode } from 'react';
import { websocketService } from '../services/websocket';
import { useAuthStore } from '../stores/authStore';
import { useConversationStore } from '../stores/conversationStore';

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages WebSocket connection lifecycle
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { accessToken, isAuthenticated } = useAuthStore();
  const {
    activeConversationId,
    appendToStreamingMessage,
    finalizeStreamingMessage,
    updateConversationTitle,
    setError,
  } = useConversationStore();

  const activeConversationRef = useRef<string | null>(null);

  // Keep ref in sync
  useEffect(() => {
    activeConversationRef.current = activeConversationId;
  }, [activeConversationId]);

  // Set up WebSocket handlers
  useEffect(() => {
    websocketService.setHandlers({
      onMessageChunk: (data) => {
        console.log('Received message chunk:', data);
        if (!data.done) {
          appendToStreamingMessage(data.conversationId, data.content);
        }
      },
      onMessageComplete: (data) => {
        console.log('Message complete:', data);
        finalizeStreamingMessage(data.conversationId, data.messageId, data.tokenCount);
      },
      onError: (data) => {
        console.error('WebSocket error:', data);
        setError(data.message);
      },
      onTitleUpdated: (data) => {
        console.log('Title updated:', data);
        updateConversationTitle(data.conversationId, data.title);
      },
      onConnect: () => {
        console.log('WebSocket connected');
        // Rejoin active conversation on reconnect
        if (activeConversationRef.current) {
          console.log('Rejoining conversation:', activeConversationRef.current);
          websocketService.joinConversation(activeConversationRef.current);
        }
      },
      onDisconnect: (reason) => {
        console.log('WebSocket disconnected:', reason);
      },
    });
  }, [appendToStreamingMessage, finalizeStreamingMessage, updateConversationTitle, setError]);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      websocketService.connect(accessToken);
    } else {
      websocketService.disconnect();
    }

    return () => {
      websocketService.disconnect();
    };
  }, [isAuthenticated, accessToken]);

  // Join/leave conversation rooms when active conversation changes
  useEffect(() => {
    if (activeConversationId) {
      console.log('Active conversation changed:', activeConversationId);
      // Wait a bit for connection to be established
      const timer = setTimeout(() => {
        if (websocketService.isConnected()) {
          console.log('Joining conversation:', activeConversationId);
          websocketService.joinConversation(activeConversationId);
        } else {
          console.log('WebSocket not connected, will join on connect');
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeConversationId]);

  return <>{children}</>;
}
